/**
 * Lane 5 wiring: assemble the sim's GameConfig from live data files.
 *
 * Data ownership: species/morphs/regions/biomes (Lane 5), balance/enclosures/
 * traps (Lane 4). This module only READS them and maps shapes - it owns no
 * numbers. Species and region JSON are parsed through the Lane 5 zod schemas
 * at load, so a bad record fails fast with a useful error. Pure mapping, no
 * DOM, no rendering imports; safe in the worker.
 *
 * Hookup (two lines wherever the worker builds its game):
 *   import { loadGameConfig } from '../config/loadGameConfig';
 *   const game = createGame(loadGameConfig(seed));
 */
import type {
  GameConfig, SpeciesEconomics, SpeciesCatalog, ParcelCatalogEntry,
  CaptureTableEntry, TrapTypeDef, MorphDef, ResearchStudyDef,
  DecisionDef, ChallengeDef, ScenarioDef, EducationProgramDef,
  LicenseDef2, JurisdictionDef2, EventDef2, GrantDef2, PartnerDef, SpecialDef,
} from '../sim/systems/types';
import { Species as SpeciesSchema, type Species as SpeciesRecord } from '../../data/schemas/species.js';
import { Region as RegionSchema, BiomeDefs as BiomeDefsSchema } from '../../data/schemas/regions.js';
import { SpeciesMorphs as SpeciesMorphsSchema, type SpeciesMorphs } from '../../data/schemas/morphs.js';
import { Biome } from '../../data/schemas/common.js';
import { catalogs } from './catalogs';

import type { AdvisorsConfig } from '../sim/systems/types';
import balanceJson from '../../data/balance.json' with { type: 'json' };
import researchJson from '../../data/research.json' with { type: 'json' };
import researchTreeJson from '../../data/research-tree.json' with { type: 'json' };
import advisorsJson from '../../data/advisors.json' with { type: 'json' };
import contractsJson from '../../data/contracts.json' with { type: 'json' };
import decisionsJson from '../../data/decisions.json' with { type: 'json' };
import challengesJson from '../../data/challenges.json' with { type: 'json' };
import hybridGoalsJson from '../../data/hybrid-goals.json' with { type: 'json' };
import scenariosJson from '../../data/scenarios.json' with { type: 'json' };
import educationJson from '../../data/education.json' with { type: 'json' };
import licensesJson from '../../data/licenses.json' with { type: 'json' };
import jurisdictionsJson from '../../data/jurisdictions.json' with { type: 'json' };
import eventsJson from '../../data/events.json' with { type: 'json' };
import grantsJson from '../../data/grants.json' with { type: 'json' };
import campaignJson from '../../data/campaign.json' with { type: 'json' };
import partnersJson from '../../data/partners.json' with { type: 'json' };
import specialsJson from '../../data/specials.json' with { type: 'json' };
import milestonesJson from '../../data/milestones.json' with { type: 'json' };
import enclosuresJson from '../../data/enclosures.json' with { type: 'json' };
import trapsJson from '../../data/traps.json' with { type: 'json' };
import biomesJson from '../../data/regions/_biomes.json' with { type: 'json' };



































import ravenCreekJson from '../../data/regions/raven_creek.json' with { type: 'json' };
import cedarBreaksJson from '../../data/regions/cedar_breaks.json' with { type: 'json' };
import saltFlatCountryJson from '../../data/regions/salt_flat_country.json' with { type: 'json' };

let speciesCache: SpeciesRecord[] | null = null;
function speciesRecordsAll(): SpeciesRecord[] {
  speciesCache ??= catalogs().species.map((raw) => SpeciesSchema.parse(raw));
  return speciesCache;
}

const REGIONS: Region[] = [
  ravenCreekJson, cedarBreaksJson, saltFlatCountryJson,
].map((raw) => RegionSchema.parse(raw));
type Region = ReturnType<typeof RegionSchema.parse>;

const BIOME_DEFS = BiomeDefsSchema.parse(biomesJson);

let morphRecords: SpeciesMorphs[] | null = null;
function morphRecordsAll(): SpeciesMorphs[] {
  morphRecords ??= catalogs().morphs.map((raw) => SpeciesMorphsSchema.parse(raw));
  return morphRecords;
}

/** Historical ids other lanes used before the catalog landed. Keep working. */
export const SPECIES_ALIASES: Record<string, string> = {
  common_ostrich: 'ostrich',
};
export function resolveSpeciesId(id: string): string {
  return SPECIES_ALIASES[id] ?? id;
}

type EncounterRarity = 'common' | 'uncommon' | 'rare' | 'very_rare';
type EconRarity = NonNullable<SpeciesEconomics['rarity']>;

/** Field encounter rarity -> Lane 4 economics rarity. */
const RARITY_MAP: Record<EncounterRarity, EconRarity> = {
  common: 'common', uncommon: 'uncommon', rare: 'rare', very_rare: 'exceptional',
};
/** Encounter rarity -> wild capture-table draw weight (Lane 4 tunes magnitudes). */
const CAPTURE_WEIGHT: Record<EncounterRarity, number> = {
  common: 100, uncommon: 35, rare: 10, very_rare: 2,
};
const TIER_ORDER: EncounterRarity[] = ['common', 'uncommon', 'rare', 'very_rare'];

function toEconomics(r: SpeciesRecord): SpeciesEconomics {
  const avgKg = (r.body.adult_weight_kg.male + r.body.adult_weight_kg.female) / 2;
  const encounters = r.field?.encounters ?? [];
  const tiers = new Set(encounters.map((e) => e.rarity));
  // Base rarity = the species' most common tier anywhere (how findable it is at best).
  const best = TIER_ORDER.find((t) => tiers.has(t)) ?? 'rare';
  const isRatite = r.class === 'flightless_bird';
  // Hoofstock carry gestation_days, ratites incubation_days (zod enforces one
  // per class); both feed the sim's generic gestationDays so ratites breed too.
  const gestationDays = r.biology.gestation_days ?? r.biology.incubation_days;
  if (gestationDays === undefined) throw new Error(`${r.id}: no gestation/incubation data`);
  return {
    id: r.id,
    name: r.common_name, // display-only source field; no new gameplay consumers
    group: isRatite ? 'ratite' : 'hoofstock',
    dietClass: isRatite ? 'ratite' : (r.behavior.forage === 'omnivore' ? 'mixed' : r.behavior.forage),
    baseValueCents: r.economics.base_price_cents,
    feedCostPerDayCents: Math.round(r.diet.feed_cost_month_cents / 30),
    adultAgeDays: r.biology.maturity_months * 30,
    rarity: RARITY_MAP[best],
    biomes: Array.from(new Set<string>(encounters.map((e) => e.biome))),
    sizeClass: avgKg < 60 ? 'small' : avgKg < 150 ? 'medium' : 'large',
    breeding: {
      gestationDays,
      litterMin: r.biology.litter.min,
      litterMax: r.biology.litter.max,
      maturityDays: r.biology.maturity_months * 30,
    },
  };
}

/** data/morphs/*.json -> inheritance/valuation records keyed by species id.
 *  Same mapping as Lane 4's catalogs.ts realMorphCatalog. */
export function loadMorphCatalog(): Record<string, MorphDef[]> {
  const out: Record<string, MorphDef[]> = {};
  for (const f of morphRecordsAll()) {
    out[f.species] = f.morphs.map((m) => {
      const def: MorphDef & { inheritance?: unknown } = {
        id: m.id,
        tier: m.tier,
        mode: m.genetics.mode === 'recessive' || m.genetics.mode === 'polygenic' ? m.genetics.mode : 'wild_type',
        wildOddsOneIn: m.wild_odds_one_in,
        ...(m.polygenic ? { polygenic: m.polygenic } : {}),
        // float multiplier -> integer bp without Math.round (lint)
        priceMultiplierBp: Math.floor(m.price_multiplier * 10000 + 0.5),
      };
      // Explicit inheritance (Lane 5 data, read by sim genotype.ts); source/quote stay in the data file.
      if (m.inheritance) {
        def.inheritance = m.inheritance.heterozygote_morph
          ? { dominance: m.inheritance.dominance, heterozygote_morph: m.inheritance.heterozygote_morph }
          : { dominance: m.inheritance.dominance };
      }
      return def;
    });
  }
  return out;
}

/** P409: meat yield per species in kg - average adult carcass weight x the
 *  dressed-share placeholder (50%; the plan specifies weight-based yield but
 *  no dressed fraction - flagged in the hunts PR). */
export const MEAT_DRESSED_SHARE_BP = 5000;
export function loadSpeciesMeatYieldKg(): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of speciesRecordsAll()) {
    const avgKg = (r.body.adult_weight_kg.male + r.body.adult_weight_kg.female) / 2;
    out[r.id] = Math.floor(avgKg * MEAT_DRESSED_SHARE_BP / 10000);
  }
  return out;
}

export function loadSpeciesCatalog(): SpeciesCatalog {
  const catalog: SpeciesCatalog = {};
  for (const r of speciesRecordsAll()) catalog[r.id] = toEconomics(r);
  // Alias entries point at the same economics so old pool ids keep working.
  for (const [alias, target] of Object.entries(SPECIES_ALIASES)) {
    catalog[alias] = { ...catalog[target], id: alias };
  }
  return catalog;
}

export function loadParcelCatalog(): ParcelCatalogEntry[] {
  const out: ParcelCatalogEntry[] = [];
  for (const region of REGIONS) {
    for (const p of region.parcels) {
      // The HQ parcel is owned via balance.parcels.hq_parcel in start modes;
      // owned catalog rows would duplicate it.
      if (p.owned) continue;
      out.push({ id: p.id, hectares: p.hectares, landClass: p.landClass, distanceKm: p.distanceKm, price: p.price });
    }
  }
  return out;
}

export function loadCaptureTable(): Record<string, CaptureTableEntry[]> {
  const table: Record<string, CaptureTableEntry[]> = {};
  for (const b of Biome.options) table[b] = [];
  for (const r of speciesRecordsAll()) {
    for (const e of r.field?.encounters ?? []) {
      table[e.biome].push({ species: r.id, weight: CAPTURE_WEIGHT[e.rarity] });
    }
  }
  return table;
}

export function loadGameConfig(seed: number): GameConfig {
  return {
    balance: balanceJson,
    enclosureTypes: enclosuresJson.types,
    species: loadSpeciesCatalog(),
    parcelCatalog: loadParcelCatalog(),
    trapTypes: trapsJson.types as unknown as Record<string, TrapTypeDef>,
    captureTable: loadCaptureTable(),
    // Lane 4-owned data, passed through by id (research studies) and verbatim (contract board params).
    // P612: the Lane 5 tree (data/research-tree.json, DESIGN DEFAULTS) merges
    // over research.json: tree nodes carry branch/tier; the five flat studies
    // get a PROVISIONAL branch assignment here until Content-Data moves it
    // into the data file (flagged to Content-Data via main).
    research: (() => {
      const legacyBranches: Partial<Record<string, string>> = {
        pasture_grasses: 'ecology', water_recycling: 'ecology',
        veterinary_protocols: 'veterinary', low_stress_handling: 'veterinary',
        breeding_nutrition: 'genetics',
      };
      const legacy = researchJson.studies.map((d) => {
        const br = legacyBranches[d.id];
        return br === undefined ? { ...d } : { ...d, branch: br }; // exactOptionalPropertyTypes
      });
      // data/schemas/validate.ts (validate:data) owns shape checking, same as trapTypes.
      const merged = [...legacy, ...researchTreeJson.nodes] as unknown as ResearchStudyDef[];
      return Object.fromEntries(merged.map((d) => [d.id, d]));
    })(),
    contracts: contractsJson,
    // P509: Content-Data advisor roster + notes (shape checked by validate:data).
    advisors: { advisors: advisorsJson.advisors, notes: advisorsJson.notes as AdvisorsConfig['notes'] },
    // Catalog parity with the full game config (QA D12): the worker is the
    // production path, and every UI projection reads these catalogs off cfg -
    // a missing catalog renders zero rows in the real game while test-only
    // config builders stay green. Mappings mirror the canonical full builder;
    // no numbers are owned here.
    decisions: Object.fromEntries((decisionsJson.decisions as unknown as DecisionDef[]).map((d) => [d.id, d])),
    challenges: Object.fromEntries([
      ...(challengesJson.challenges as unknown as ChallengeDef[]),
      // hybrid-goals.json weekly challenges (PLACEHOLDER rewards); cooldown
      // chosen engine-side: one week, matching their weekly cadence.
      ...(hybridGoalsJson.challenges as unknown as Array<{ id: string; name: string; kind: 'weekly'; description: string; requirement: unknown; reward_cash_cents: number }>)
        .map((c) => ({ id: c.id, name: c.name, kind: c.kind, cooldown_days: 7, requirement: c.requirement, reward: { cash_cents: c.reward_cash_cents } })),
    ].map((d) => [d.id, d])),
    regions: Object.fromEntries([...REGIONS].sort((a, b) => a.id.localeCompare(b.id)).map((r) => [r.id, { id: r.id, name: r.name, startingRegion: r.starting_region }])),
    scenarios: Object.fromEntries((scenariosJson.scenarios as unknown as ScenarioDef[]).map((d) => [d.id, d])),
    education: Object.fromEntries((educationJson.programs as unknown as EducationProgramDef[]).map((d) => [d.id, d])),
    licenses: Object.fromEntries((licensesJson.licenses as unknown as LicenseDef2[]).map((d) => [d.id, d])),
    expedite: licensesJson.expedite, // P709
    jurisdictions: Object.fromEntries((jurisdictionsJson.jurisdictions as unknown as JurisdictionDef2[]).map((d) => [d.id, d])),
    events: Object.fromEntries((eventsJson.events as unknown as EventDef2[]).map((d) => [d.id, d])),
    grants: Object.fromEntries((grantsJson.grants as unknown as GrantDef2[]).map((d) => [d.id, d])),
    campaign: { reserves: campaignJson.reserves },
    partners: Object.fromEntries((partnersJson.partners as unknown as PartnerDef[]).map((d) => [d.id, d])),
    specials: Object.fromEntries((specialsJson.specials as unknown as SpecialDef[]).map((d) => [d.id, d])),
    milestones: milestonesJson,
    seed,
  };
}

/** Display helpers for UI/render lanes: full content records by id.
 *  Lazily materialized on first access (catalogs load after the first scene). */
let speciesMap: Record<string, SpeciesRecord> | null = null;
function speciesMapAll(): Record<string, SpeciesRecord> {
  speciesMap ??= Object.fromEntries(speciesRecordsAll().map((r) => [r.id, r]));
  return speciesMap;
}
export const speciesRecords: Record<string, SpeciesRecord> = new Proxy<Record<string, SpeciesRecord>>({}, {
  get: (_t, p: string) => speciesMapAll()[p],
  has: (_t, p: string) => p in speciesMapAll(),
  ownKeys: () => Reflect.ownKeys(speciesMapAll()),
  getOwnPropertyDescriptor: (_t, p: string) => Object.getOwnPropertyDescriptor(speciesMapAll(), p),
});
export function getSpeciesRecord(id: string): SpeciesRecord | undefined {
  return speciesMapAll()[resolveSpeciesId(id)];
}
export function getDefaultCoat(id: string): string {
  return getSpeciesRecord(id)?.appearance.default_coat ?? `${resolveSpeciesId(id)}_wild`;
}
export { BIOME_DEFS as biomeDefs };
